import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { useListeClavier } from '../hooks/useListeClavier';
import { FormulaireReglement } from './PaiementsPage';
import { peutModifierReglement } from '../lib/reglement';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Champ, Chargement, EnTetePage, Erreur, Infobulle, Modale } from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { ChampMembre, type SaisieMembre } from '../components/ChampMembre';
import { ChampHandicap, type SaisieHandicap } from '../components/ChampHandicap';
import { ChampDroitImage } from '../components/ChampDroitImage';
import { ChampEtablissement } from '../components/ChampEtablissement';
import { EditeurTexteLeger } from '../components/EditeurTexteLeger';
import { estMineur } from '../lib/age';
import {
  bilanDroitImage,
  resumerDroitImage,
  saisieDepuisDroitImage,
  type SaisieDroitImage,
} from '../lib/droitImage';
import { enregistrerDroitImage } from '../hooks/useDroitImage';
import { libelleHandicap } from '../lib/handicap';
import { enregistrerMembre } from '../hooks/useAdhesion';
import { FormulaireFinAnticipee, FormulaireInscription } from './InscriptionsPage';
import { FormulaireTransfert } from '../components/FormulaireTransfert';
import { FormulaireRemboursement } from '../components/FormulaireRemboursement';
import { FormulaireAttestation } from '../components/FormulaireAttestation';
import { decrireReductions } from '../lib/reductions';
import { ChampTelephone } from '../components/ChampTelephone';
import { formaterTelephone, telephonePourSaisie } from '../lib/telephone';
import {
  formaterDate,
  formaterDuree,
  formaterHeure,
  formaterMontant,
  libelleJour,
  libelleStatut,
} from '../lib/format';
import type {
  Activite,
  Adresse,
  AdresseRattachee,
  Contact,
  ContactRattache,
  Creneau,
  Inscription,
  Lien,
  Personne,
  PersonneDetail,
  Reglement,
  QualiteContact,
  TypeLien,
} from '../types';

const LIBELLE_MODE_REGLEMENT: Record<string, string> = {
  cheque: 'Chèque',
  virement: 'Virement',
  especes: 'Espèces',
  cheque_vacances: 'Chèque vacances',
  carte: 'Carte',
};

const QUALITES: QualiteContact[] = ['mere', 'pere', 'tuteur', 'conjoint', 'eleve', 'autre'];
/**
 * Les relations telles qu'on les enonce, pas telles qu'on les stocke.
 *
 * La base ne connait que quatre types, dont deux ORIENTES : « parent » se
 * lit « A est le parent de B ». Vu de l'autre bout, c'est « enfant de » —
 * le meme lien, pris par l'autre extremite. L'ecran offre donc six choix
 * pour quatre types, `inverse` disant par quel bout on parle.
 *
 * C'est ce qui permet de corriger un sens sans supprimer puis recreer.
 */
const RELATIONS: {
  cle: string;
  court: string;
  libelle: string;
  type: TypeLien;
  inverse: boolean;
  aide: string;
}[] = [
  {
    cle: 'fratrie',
    court: 'frère / sœur',
    libelle: 'Frère / sœur',
    type: 'fratrie',
    inverse: false,
    aide: 'Lien symétrique : une seule saisie suffit.',
  },
  {
    cle: 'conjoint',
    court: 'conjoint',
    libelle: 'Conjoint',
    type: 'conjoint',
    inverse: false,
    aide: 'Lien symétrique.',
  },
  {
    cle: 'parent',
    court: 'parent de',
    libelle: 'Parent de…',
    type: 'parent',
    inverse: false,
    aide: 'Cet adhérent est le parent de la personne choisie.',
  },
  {
    cle: 'enfant',
    court: 'enfant de',
    libelle: 'Enfant de…',
    type: 'parent',
    inverse: true,
    aide: 'Cet adhérent est l’enfant de la personne choisie.',
  },
  {
    cle: 'tuteur',
    court: 'tuteur de',
    libelle: 'Tuteur de…',
    type: 'tuteur',
    inverse: false,
    aide: 'Cet adhérent est le tuteur de la personne choisie.',
  },
  {
    cle: 'pupille',
    court: 'sous tutelle de',
    libelle: 'Sous la tutelle de…',
    type: 'tuteur',
    inverse: true,
    aide: 'Cet adhérent est sous la tutelle de la personne choisie.',
  },
];

/** Retrouve la relation enoncable d'un lien tel qu'il revient du serveur. */
function relationDe(lien: Lien): string {
  if (lien.type === 'fratrie' || lien.type === 'conjoint') return lien.type;
  return RELATIONS.find((r) => r.type === lien.type && r.inverse === (lien.sens === 'cible'))!.cle;
}

function Section({
  titre,
  description,
  action,
  children,
}: {
  titre: string;
  /** ReactNode et non `string` : le bandeau financier des inscriptions y
      met des montants colores. */
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="carte mb-6 overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-encre-200 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-encre-500">{titre}</h2>
          {description && <p className="mt-0.5 text-xs text-encre-400">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * Bloc vide — une bande en pointilles, plutot qu'une carte creuse.
 *
 * Une fiche sans contact, sans lien, sans adresse ou sans reglement est le
 * cas ORDINAIRE : 266 adherents sur 292 sont inscrits seuls. Repeter pour
 * chacun quatre sections a en-tete pleine et corps vide fait defiler la
 * page sans rien apprendre. La bande grise dit la meme chose en une ligne,
 * et garde le bouton d'ajout a portee.
 */
function SectionVide({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-encre-200 px-4 py-3">
      <p className="text-sm text-encre-500">{message}</p>
      {action}
    </div>
  );
}

export function AdherentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { peut, entiteActive } = useAuth();
  const naviguer = useNavigate();
  const [modale, setModale] = useState<'contact' | 'adresse' | 'lien' | 'fiche' | 'commentaire' | null>(null);
  // Le clic sur une ligne ouvre la MEME modale, avec l'objet a modifier.
  const [editionAdresse, setEditionAdresse] = useState<AdresseRattachee | null>(null);
  const [editionLien, setEditionLien] = useState<Lien | null>(null);
  const [editionContact, setEditionContact] = useState<ContactRattache | null>(null);
  const annee = useAnnee();

  // L'entite ET l'annee font partie de la cle : le statut de membre, les
  // inscriptions et les reglements se calculent DANS un scope et SUR une
  // annee. Sans l'entite, basculer d'ecole laissait la fiche telle quelle
  // — Zoe restait « usagere » sur Domusic ; sans l'annee, elle restait
  // usagere en 2026-27 ou elle n'a aucune inscription.
  // Compteur de reglements enregistres : il traverse la page pour forcer
  // le bloc Inscriptions a relire ses montants payes.
  const [versionPaiements, setVersionPaiements] = useState(0);
  // Ce qui est arrive pour cet adherent sans etre encore ventile : il
  // entre dans son « payé », donc dans le calcul du reste et du
  // trop-percu. Il vit dans le bloc Paiements, il se lit dans le bloc
  // Inscriptions — d'ou ce relais.
  const [nonAffecte, setNonAffecte] = useState(0);
  // Un forfait d'essai en cours, publie par le bloc Inscriptions
  // (2026-11-04) : le bloc Paiements n'offre pas l'attestation tant qu'un
  // essai n'est ni confirme ni arrete. (Du 2026-11-03 au 2026-11-04, c'est
  // le montant du qui etait publie, pour n'attester qu'une annee soldee.)
  const [essaiEnCours, setEssaiEnCours] = useState(false);

  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<PersonneDetail>(`/api/personnes/${id}`, { anneeId: annee.anneeId }),
    [id, entiteActive?.id, annee.anneeId],
  );

  if (chargement) return <Chargement />;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees) return null;

  const detacherContact = async (contactId: string) => {
    await api.delete(`/api/personnes/${donnees.id}/contacts/${contactId}`);
    recharger();
  };

  const definirReferent = async (contactId: string) => {
    await api.patch(`/api/personnes/${donnees.id}/contacts/${contactId}`, {
      estReferent: true,
    });
    recharger();
  };

  const detacherAdresse = async (adresseId: string) => {
    await api.delete(`/api/personnes/${donnees.id}/adresses/${adresseId}`);
    recharger();
  };

  const supprimerLien = async (lienId: string) => {
    await api.delete(`/api/liens/${lienId}`);
    recharger();
  };

  return (
    <>
      <button
        type="button"
        className="mb-4 text-sm text-encre-500 underline"
        onClick={() => naviguer('/adherents')}
      >
        ← Retour aux adhérents
      </button>

      <EnTetePage
        titre={
          <>
            {donnees.nom.toUpperCase()} {donnees.prenom}
            {/* RG-07 revisée : le statut se lit à côté du nom, là où on
                cherche qui est qui. */}
            {/* Le statut de membre se lit DANS une entite : la fiche est
                partagee, le statut ne l'est pas. Sans inscription ni
                adhesion ici, il n'y a RIEN a annoncer — pas meme
                l'absence : dire « usager » d'une eleve d'Artistic sur
                Domusic etait faux, le dire autrement resterait du bruit
                sur une fiche qu'on ne consulte pas pour ca. */}
            {!donnees.adherent_entite ? null : donnees.membre_actif ? (
              <Badge ton="succes">Membre actif</Badge>
            ) : (
              /* « Usager » dit ce qu'on est PAR DEFAUT quand on frequente
                 l'ecole. Sur quelqu'un qui a arrete, l'etiquette ment : on
                 lui prefere son inactivite, seule chose a retenir de lui. */
              donnees.actif && <Badge>Usager</Badge>
            )}
            {/* La fiche ne disait nulle part qu'un adherent a arrete — elle
                etait identique a celle d'un adherent en cours. Un membre
                actif inactif n'est pas une contradiction : REBECCHI Fabrice
                a cotise puis cesse ses cours, et les deux pastilles le
                disent. */}
            {!donnees.actif && <Badge ton="alerte">Inactif</Badge>}
            {/* La situation declaree se lit a cote du nom : c'est ce qu'un
                professeur doit savoir avant d'ouvrir le reste. */}
            {donnees.handicap && <Badge ton="contour">{libelleHandicap(donnees.handicap)}</Badge>}
            {/* Le droit a l'image de l'annee (2026-10-13), pour qui
                photographie : refus en rouge, accord en creux. Rien tant
                que rien n'est saisi — l'ecran Anomalies le dit deja, une
                pastille « ? » de plus encombrait la fiche. */}
            {(() => {
              const bilan = bilanDroitImage(donnees.droit_image);
              const detail = resumerDroitImage(donnees.droit_image);
              if (bilan === 'refuse') {
                return (
                  <Infobulle texte={detail}>
                    <Badge ton="danger">Image refusée</Badge>
                  </Infobulle>
                );
              }
              if (bilan === 'accorde') {
                return (
                  <Infobulle texte={detail}>
                    <Badge ton="succesContour">Image accordée</Badge>
                  </Infobulle>
                );
              }
              return null;
            })()}
          </>
        }
        description={[
          donnees.date_naissance
            ? `Né(e) le ${formaterDate(donnees.date_naissance)} — ${donnees.age} ans (${donnees.tranche_age})`
            : 'Date de naissance inconnue',
          // L'etablissement, et son etat quand la Direction ne l'a pas
          // encore valide (2026-10-13).
          donnees.ecole_frequentee &&
            `${donnees.ecole_frequentee}${donnees.etablissement_valide === false ? ' (à valider)' : ''}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          peut('personne.modifier') ? (
            <button type="button" className="bouton-secondaire" onClick={() => setModale('fiche')}>
              Modifier la fiche
            </button>
          ) : null
        }
      />

      {/* Coordonnées de l'adhérent lui-même — à distinguer de ses
          contacts, qui sont des tiers. */}
      {(donnees.email || donnees.telephone) && (
        <div className="carte mb-6 flex flex-wrap gap-x-8 gap-y-2 p-4 text-sm">
          {donnees.email && (
            <div>
              <span className="text-xs text-encre-400">Email</span>
              <div>{donnees.email}</div>
            </div>
          )}
          {donnees.telephone && (
            <div>
              <span className="text-xs text-encre-400">Téléphone</span>
              <div className="tabulaire">{formaterTelephone(donnees.telephone, donnees.telephone_pays)}</div>
            </div>
          )}
        </div>
      )}

      {/* --- Inscriptions ---------------------------------------------- */}
      <InscriptionsDeLAdherent
        personne={donnees}
        onChange={recharger}
        version={versionPaiements}
        nonAffecte={nonAffecte}
        onEssai={setEssaiEnCours}
      />

      {/* --- Paiements -------------------------------------------------- */}
      <PaiementsDeLAdherent
        personne={donnees}
        onNonAffecte={setNonAffecte}
        essaiEnCours={essaiEnCours}
        onChange={() => {
          // La fiche pour la part cotisation, le compteur pour les
          // inscriptions : les deux portent des montants que le
          // reglement vient de changer.
          recharger();
          setVersionPaiements((n) => n + 1);
        }}
      />

      {/* --- Commentaires ----------------------------------------------
          Un texte libre a mise en forme legere (2026-10-31), entre les
          paiements et les adresses — demande de Georges. Le serveur a
          filtre ce qu'il contient a l'enregistrement (paragraphes, gras,
          italique, souligne, alignement, rien d'autre) : il se rend tel
          quel. La fiche etant partagee, le commentaire l'est aussi. */}
      {donnees.commentaire ? (
        <Section
          titre="Commentaires"
          action={
            peut('personne.modifier') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('commentaire')}
              >
                Modifier
              </button>
            ) : null
          }
        >
          <div
            className="texte-riche px-4 py-3 text-sm text-encre-700"
            dangerouslySetInnerHTML={{ __html: donnees.commentaire }}
          />
        </Section>
      ) : (
        <SectionVide
          message="Aucun commentaire."
          action={
            peut('personne.modifier') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('commentaire')}
              >
                Ajouter un commentaire
              </button>
            ) : null
          }
        />
      )}

      {/* --- Adresses --------------------------------------------------
          Meme traitement que les contacts et les liens : sans adresse, le
          bloc n'a rien a montrer. */}
      {donnees.adresses.length === 0 ? (
        <SectionVide
          message="Aucune adresse renseignée. Aucun document postal ne pourra lui être adressé."
          action={
            peut('adresse.gerer') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('adresse')}
              >
                Ajouter une adresse
              </button>
            ) : null
          }
        />
      ) : (
        <Section
          titre={`Adresses (${donnees.adresses.length})`}
          description="Une adresse primaire, et autant de secondaires que nécessaire — la garde alternée en demande deux."
          action={
            peut('adresse.gerer') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('adresse')}
              >
                Ajouter une adresse
              </button>
            ) : null
          }
        >
          <ul className="divide-y divide-encre-100">
            {donnees.adresses.map((adresse) => (
              <li
                key={adresse.id}
                className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                  peut('adresse.gerer') ? 'cursor-pointer hover:bg-encre-50' : ''
                }`}
                onClick={peut('adresse.gerer') ? () => setEditionAdresse(adresse) : undefined}
              >
                {/* Une seule ligne : rue, code postal et ville se lisent
                      d'un trait, comme on les ecrit sur une enveloppe. */}
                <div className="min-w-0 flex-1">
                  {/* Memes classes que le bloc Paiements : la rue a la
                        taille du mode de reglement, le code postal et la
                        ville a celle — et a la couleur — du n° de cheque. */}
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="truncate">{adresse.ligne1}</span>
                    {(adresse.code_postal || adresse.ville) && (
                      <span className="tabulaire text-xs text-encre-500">
                        {[adresse.code_postal, adresse.ville].filter(Boolean).join(' ')}
                      </span>
                    )}
                    <Badge ton={adresse.rang === 'primaire' ? 'succes' : 'neutre'}>
                      {adresse.rang}
                    </Badge>
                    {(adresse.nb_rattachements ?? 1) > 1 && (
                      <Badge ton="alerte">
                        partagée avec {(adresse.nb_rattachements ?? 1) - 1} autre
                        {(adresse.nb_rattachements ?? 1) > 2 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                  {adresse.ligne2 && <div className="text-xs text-encre-500">{adresse.ligne2}</div>}
                  {adresse.commentaire && (
                    <div className="text-xs text-encre-400">{adresse.commentaire}</div>
                  )}
                </div>

                {peut('adresse.gerer') && (
                  <button
                    type="button"
                    className="text-sm text-red-600 underline"
                    onClick={(evenement) => {
                      evenement.stopPropagation();
                      void detacherAdresse(adresse.id);
                    }}
                  >
                    Détacher
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* --- Contacts --------------------------------------------------
          Meme traitement que la famille : sans contact, le bloc n'a rien a
          montrer. Une bande en pointilles suffit, plutot qu'une section
          vide repetee sur toutes les fiches qui n'en ont pas. */}
      {donnees.contacts.length === 0 ? (
        <SectionVide
          message="Aucun contact. Aucun envoi ciblé ne pourra atteindre cet adhérent."
          action={
            peut('contact.gerer') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('contact')}
              >
                Rattacher un contact
              </button>
            ) : null
          }
        />
      ) : (
        <Section
          titre={`Contacts (${donnees.contacts.length})`}
          description="Un contact est une entité partagée : le corriger ici le corrige pour tous les adhérents qui s’y rattachent. Les contacts des frères et sœurs apparaissent aussi, marqués « via » : ils se modifient sur la fiche qui les porte."
          action={
            peut('contact.gerer') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('contact')}
              >
                Rattacher un contact
              </button>
            ) : null
          }
        >
          <ul className="divide-y divide-encre-100">
            {donnees.contacts.map((contact) => (
              // Un contact vu par la fratrie (`via`) n'a ni clic ni
              // actions ici : rien n'est rattache a cette fiche, tout se
              // regle sur celle du frere ou de la soeur.
              <li
                key={contact.id}
                className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                  peut('contact.gerer') && !contact.via ? 'cursor-pointer hover:bg-encre-50' : ''
                } ${contact.via ? 'text-encre-600' : ''}`}
                onClick={
                  peut('contact.gerer') && !contact.via
                    ? () => setEditionContact(contact)
                    : undefined
                }
              >
                {/* Deux colonnes de largeur fixe : le nom et ses tags d'un
                      cote, les coordonnees de l'autre. Les lignes s'alignent
                      ainsi entre elles, ce qu'un empilement ne donnait pas. */}
                <div className="flex min-w-0 flex-[3] flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">
                    {contact.prenom ?? ''} <span className="uppercase">{contact.nom ?? ''}</span>
                    {!contact.prenom && !contact.nom && '—'}
                  </span>
                  <Badge>{contact.qualite}</Badge>
                  {contact.via && (
                    <Badge ton="contour">
                      via {contact.via.prenom} {contact.via.nom.toUpperCase()}
                    </Badge>
                  )}
                  {contact.est_referent && <Badge ton="succes">Référent</Badge>}
                  {!contact.via && (contact.nb_rattachements ?? 1) > 1 && (
                    <Badge ton="alerte">
                      partagé avec {(contact.nb_rattachements ?? 1) - 1} autre
                      {(contact.nb_rattachements ?? 1) > 2 ? 's' : ''}
                    </Badge>
                  )}
                </div>

                {/* Meme traitement que le n° de cheque : 12 px, gris. */}
                <div className="min-w-0 flex-[2] truncate text-xs text-encre-500">
                  {[formaterTelephone(contact.telephone, contact.telephone_pays), contact.email]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </div>

                {/* Largeur FIXE : « Definir referent » n'apparait que sur les
                      contacts qui ne le sont pas. Sans largeur imposee, les
                      deux colonnes precedentes se partagent un reste
                      different d'une ligne a l'autre, et les coordonnees ne
                      s'alignent plus. */}
                {peut('contact.gerer') && !contact.via && (
                  <div className="flex w-48 shrink-0 justify-end gap-3">
                    {!contact.est_referent && (
                      <button
                        type="button"
                        className="text-sm text-encre-500 underline hover:text-encre-900"
                        onClick={(evenement) => {
                          evenement.stopPropagation();
                          void definirReferent(contact.id);
                        }}
                      >
                        Définir référent
                      </button>
                    )}
                    <button
                      type="button"
                      className="text-sm text-red-600 underline"
                      onClick={(evenement) => {
                        evenement.stopPropagation();
                        void detacherContact(contact.id);
                      }}
                    >
                      Détacher
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* --- Famille ---------------------------------------------------
          Une famille suppose au moins deux membres reliés : sans lien, le
          bloc n'a rien à montrer. On ne laisse qu'un bouton, plutôt qu'une
          section vide répétée sur 266 fiches. */}
      {donnees.liens.length === 0 ? (
        <SectionVide
          message="Cet adhérent n’a aucun lien de parenté enregistré."
          action={
            peut('lien.gerer') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('lien')}
              >
                Ajouter un lien
              </button>
            ) : null
          }
        />
      ) : (
        <Section
          titre={`Famille — ${donnees.famille.length + 1} membres`}
          description="Une famille n’est pas une fiche : c’est l’ensemble des adhérents reliés de proche en proche."
          action={
            peut('lien.gerer') ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModale('lien')}
              >
                Ajouter un lien
              </button>
            ) : null
          }
        >
          <ul className="divide-y divide-encre-100">
            {donnees.liens.map((lien) => (
              <li
                key={lien.id}
                className={`flex items-center justify-between gap-3 px-4 py-3 ${
                  peut('lien.gerer') ? 'cursor-pointer hover:bg-encre-50' : ''
                }`}
                onClick={peut('lien.gerer') ? () => setEditionLien(lien) : undefined}
              >
                {/* Prenom NOM puis le tag, comme les contacts et les
                    adresses : les trois blocs se lisent de la meme facon. */}
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/adherents/${lien.personne_id}`}
                    className="font-medium underline"
                    onClick={(evenement) => evenement.stopPropagation()}
                  >
                    {lien.prenom} <span className="uppercase">{lien.nom}</span>
                  </Link>
                  {/* La pastille enonce la relation dans le sens ou on la
                      lit depuis CETTE fiche : « parent de » d'un cote,
                      « enfant de » de l'autre. */}
                  <Badge>{RELATIONS.find((r) => r.cle === relationDe(lien))!.court}</Badge>
                </div>
                {peut('lien.gerer') && (
                  <button
                    type="button"
                    className="text-sm text-red-600 underline"
                    onClick={(evenement) => {
                      evenement.stopPropagation();
                      void supprimerLien(lien.id);
                    }}
                  >
                    Retirer
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <FormulaireFiche
        ouverte={modale === 'fiche'}
        personne={donnees}
        onFermer={() => setModale(null)}
        onEnregistre={() => {
          setModale(null);
          recharger();
        }}
      />

      <FormulaireCommentaire
        ouverte={modale === 'commentaire'}
        personne={donnees}
        onFermer={() => setModale(null)}
        onEnregistre={() => {
          setModale(null);
          recharger();
        }}
      />

      <FormulaireContact
        ouverte={modale === 'contact' || editionContact !== null}
        personneId={donnees.id}
        contact={editionContact}
        onFermer={() => {
          setModale(null);
          setEditionContact(null);
        }}
        onEnregistre={() => {
          setModale(null);
          setEditionContact(null);
          recharger();
        }}
      />
      <FormulaireAdresse
        ouverte={modale === 'adresse' || editionAdresse !== null}
        personneId={donnees.id}
        adresse={editionAdresse}
        onFermer={() => {
          setModale(null);
          setEditionAdresse(null);
        }}
        onEnregistre={() => {
          setModale(null);
          setEditionAdresse(null);
          recharger();
        }}
      />
      <FormulaireLien
        ouverte={modale === 'lien' || editionLien !== null}
        personne={donnees}
        lien={editionLien}
        onFermer={() => {
          setModale(null);
          setEditionLien(null);
        }}
        onEnregistre={() => {
          setModale(null);
          setEditionLien(null);
          recharger();
        }}
      />
    </>
  );
}

/**
 * Paiements de l'adhérent — ce qui a été encaissé, et ce qui reste à venir.
 *
 * Un règlement couvre des INSCRIPTIONS, pas une personne : ceux listés ici
 * sont ceux dont la ventilation touche cet adhérent. Un chèque familial y
 * apparaît donc sur chacun des enfants qu'il paie, pour la part qui les
 * concerne — c'est ce que la ventilation permet de dire, et qu'un
 * rattachement à un nom de famille ne permettait pas.
 */
function PaiementsDeLAdherent({
  personne,
  onChange,
  onNonAffecte,
  essaiEnCours,
}: {
  personne: PersonneDetail;
  /** Un forfait d'essai de l'adherent est en cours ou echu sans etre confirme : pas d'attestation (2026-11-04). */
  essaiEnCours: boolean;
  /**
   * La part RECUE MAIS NON VENTILEE des reglements qui ne couvrent QUE
   * cet adherent. Elle entre dans son « payé » : l'argent est arrive
   * pour lui, meme s'il n'est encore affecte a rien.
   *
   * Restreinte aux reglements a UN seul beneficiaire, et ce n'est pas un
   * detail : 27 des 727 reglements de 2025-26 en couvrent plusieurs.
   * Le reliquat d'un cheque familial n'appartient a personne en
   * particulier — l'attribuer creerait un trop-percu sur chacune des
   * fiches qu'il touche.
   */
  onNonAffecte: (montant: number) => void;
  /**
   * Un reglement enregistre change ce que les INSCRIPTIONS affichent —
   * « payé », « reste à payer », et le bandeau financier. Le bloc
   * Inscriptions a sa propre requete : sans ce signal, il gardait les
   * montants d'avant le paiement (constate le 2026-09-01). La fiche
   * elle-meme est rechargee au passage, pour la part cotisation.
   */
  onChange: () => void;
}) {
  const { peut } = useAuth();
  const [saisieOuverte, setSaisieOuverte] = useState(false);
  // Le clic sur une ligne ouvre le MEME formulaire, avec le reglement —
  // comme les inscriptions juste au-dessus.
  const [enModification, setEnModification] = useState<Reglement | null>(null);
  // Rembourser un trop-percu (2026-10-09) : Direction.
  const [remboursementOuvert, setRemboursementOuvert] = useState(false);
  // L'attestation de paiement (2026-10-31) : un PDF des reglements
  // encaisses, enregistre puis joint a un message Gmail.
  const [attestationOuverte, setAttestationOuverte] = useState(false);

  // L'annee vient du BANDEAU, comme partout : la fiche montrait les
  // reglements de l'annee active meme quand une autre etait choisie.
  const annee = useAnnee();

  // RG-27 : rien ne s'ecrit sur une annee clouturee — le bouton ne se
  // propose donc pas. Un reglement couvre des INSCRIPTIONS : il en faut
  // sur l'annee affichee pour qu'il y ait quelque chose a ventiler.
  const peutSaisir = peut('reglement.creer') && annee.modifiable && annee.annee !== null;

  const reglements = useRessource(
    () =>
      annee.annee && peut('reglement.lire')
        ? api.get<Reglement[]>('/api/reglements', {
            anneeId: annee.anneeId,
            personneId: personne.id,
            limite: 200,
          })
        : Promise.resolve([]),
    [annee.anneeId, personne.id],
  );

  /**
   * Publie par un EFFET et non pendant le rendu : poser l'etat du parent
   * depuis le rendu de l'enfant n'est pas garanti par React — piege deja
   * rencontre sur la ventilation, le 2026-09-01.
   *
   * Calcule AVANT le retour anticipe : un hook ne se declare pas apres.
   */
  const nonAffecte = (reglements.donnees ?? [])
    .filter((r) => r.nb_beneficiaires <= 1 && r.statut !== 'incident')
    .reduce((somme, r) => somme + Math.max(r.montant_net - r.montant_ventile, 0), 0);
  const publierNonAffecte = useRef(onNonAffecte);
  publierNonAffecte.current = onNonAffecte;
  useEffect(() => {
    publierNonAffecte.current(nonAffecte);
  }, [nonAffecte]);

  if (!peut('reglement.lire')) return null;

  const liste = reglements.donnees ?? [];
  const encaisses = liste.filter((r) => r.statut === 'encaisse');

  const aVenir = liste.filter((r) => r.statut !== 'encaisse');
  const somme = (lignes: Reglement[]) => lignes.reduce((total, r) => total + r.montant_net, 0);

  // Toute ligne s'ouvre (2026-10-07) : verrouillee, le formulaire
  // s'affiche en lecture seule et dit pourquoi.
  const ligne = (r: Reglement) => (
    <Infobulle
      key={r.id}
      balise="li"
      texte={peutModifierReglement(r, peut) ? 'Modifier le règlement' : 'Voir le détail du règlement'}
      className="flex flex-wrap cursor-pointer items-center gap-3 px-4 py-3 hover:bg-encre-50"
      onClick={() => setEnModification(r)}
    >
      <span className="tabulaire w-24 text-sm text-encre-500">
        {formaterDate(r.date_encaissement)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>{LIBELLE_MODE_REGLEMENT[r.mode] ?? r.mode}</span>
          {r.numero_cheque && (
            <span className="tabulaire text-xs text-encre-500">n° {r.numero_cheque}</span>
          )}
          {r.montant < 0 && <Badge ton="danger">remboursement</Badge>}
          {r.statut === 'encaisse' ? (
            <Badge ton="succes">encaissé</Badge>
          ) : r.statut === 'incident' ? (
            <Badge ton="danger">incident</Badge>
          ) : r.statut === 'en_cours' ? (
            <Badge ton="neutre">en cours</Badge>
          ) : (
            <Badge ton="alerte">prévu</Badge>
          )}
          {/* LA PART NON VENTILEE, comme dans la liste des paiements.
              Sans elle la fiche disait deux chiffres sans expliquer leur
              ecart : « Payé 2 270 € » au bloc Inscriptions (les
              allocations) contre « 2 281 € encaissés » ici (les montants
              recus). Les 11 EUR de difference n'etaient affectes a rien,
              et rien ne le montrait. Constate le 2026-09-02. */}
          {r.montant_ventile < r.montant_net && (
            <Badge ton="alerte">
              reste à allouer {formaterMontant(r.montant_net - r.montant_ventile)}
            </Badge>
          )}
          {/* UN CHEQUE FAMILIAL (RG-15) : ce qu'il alloue AUX AUTRES se dit
              ici, SUR LA MEME LIGNE que les pastilles pour ne pas rehausser
              la ligne (2026-10-08) — BELLIER Honore. */}
          {r.nb_beneficiaires > 1 && (
            <span className="text-xs text-encre-400">
              {r.allocations
                .filter((a) => a.personne_id !== personne.id)
                .map((a) => `${formaterMontant(a.montant)} alloué à ${a.nom.toUpperCase()} ${a.prenom}`)
                .join(' · ')}
            </span>
          )}
        </div>
      </div>
      <span className={`tabulaire text-sm font-medium ${r.montant < 0 ? 'text-red-700' : ''}`}>
        {formaterMontant(r.montant_net)}
      </span>
    </Infobulle>
  );

  // Rien a montrer tant que la requete court : une bande « aucun reglement »
  // qui se remplirait une seconde plus tard dirait le contraire du vrai.
  if (reglements.chargement) {
    return (
      <Section
        titre="Paiements"
        description="Les règlements couvrant les inscriptions de cet adhérent."
      >
        <Chargement />
      </Section>
    );
  }

  // Sans reglement, il n'y a ni total ni ligne : la bande grise suffit.
  // Aucun bouton — un reglement se saisit depuis l'ecran Paiements, ou il
  // peut couvrir plusieurs adherents a la fois.
  const peutRembourser = peut('reglement.rembourser') && annee.modifiable && annee.annee !== null;
  // L'attestation n'a de sens que sur de l'argent recu : sans reglement
  // recu sur l'annee — encaisse, ou cheque remis pas encore encaisse
  // (2026-11-04) ; un cheque en incident n'est pas recu —, le bouton se
  // montre mais ne fait rien, et dit pourquoi. Et pas pendant un forfait
  // d'essai. La regle du solde du 2026-11-03 est levee : le PDF dit
  // lui-meme le montant du, ce qui est recu et le reste a payer. Le
  // serveur applique les memes refus (409).
  const peutAttester = annee.annee !== null && liste.length > 0;
  const recus = liste.filter((r) => r.statut !== 'incident');
  const attestationPossible = recus.length > 0 && !essaiEnCours;
  const motifAttestation =
    recus.length === 0
      ? 'Aucun règlement reçu sur l’année'
      : essaiEnCours
        ? 'Pas d’attestation tant qu’un forfait d’essai n’est pas confirmé ou arrêté'
        : 'Attestation de paiement en PDF';
  const bouton =
    peutSaisir || peutRembourser || peutAttester ? (
      <span className="flex gap-2">
        {peutAttester && (
          <Infobulle texte={motifAttestation} className="inline-flex">
            <button
              type="button"
              className="bouton-secondaire disabled:pointer-events-none"
              disabled={!attestationPossible}
              onClick={() => setAttestationOuverte(true)}
            >
              Attestation
            </button>
          </Infobulle>
        )}
        {peutRembourser && (
          <button type="button" className="bouton-secondaire" onClick={() => setRemboursementOuvert(true)}>
            Rembourser
          </button>
        )}
        {peutSaisir && (
          <button type="button" className="bouton-secondaire" onClick={() => setSaisieOuverte(true)}>
            Ajouter un paiement
          </button>
        )}
      </span>
    ) : null;

  // Le formulaire est CELUI de l'ecran Paiements, avec l'adherent deja
  // pose en beneficiaire : un reglement peut en couvrir plusieurs
  // (RG-15), et la saisie doit pouvoir le dire ici aussi.
  const formulaire = annee.annee ? (
    <>
      <FormulaireReglement
        ouverte={saisieOuverte}
        anneeId={annee.anneeId}
        anneeLibelle={annee.annee.libelle}
        personneInitiale={personne}
        onFermer={() => setSaisieOuverte(false)}
        onEnregistre={() => {
          setSaisieOuverte(false);
          reglements.recharger();
          onChange();
        }}
      />

      <FormulaireReglement
        ouverte={enModification !== null}
        anneeId={annee.anneeId}
        anneeLibelle={annee.annee.libelle}
        reglement={enModification}
        onFermer={() => setEnModification(null)}
        onEnregistre={() => {
          setEnModification(null);
          reglements.recharger();
          onChange();
        }}
      />

      <FormulaireRemboursement
        ouverte={remboursementOuvert}
        personne={personne}
        anneeId={annee.anneeId}
        onFermer={() => setRemboursementOuvert(false)}
        onEnregistre={() => {
          setRemboursementOuvert(false);
          reglements.recharger();
          onChange();
        }}
      />

      <FormulaireAttestation
        ouverte={attestationOuverte}
        personne={personne}
        anneeId={annee.anneeId}
        anneeLibelle={annee.annee.libelle}
        onFermer={() => setAttestationOuverte(false)}
      />
    </>
  ) : null;

  if (liste.length === 0) {
    return (
      <>
        <SectionVide message="Aucun règlement rattaché à cet adhérent." action={bouton} />
        {formulaire}
      </>
    );
  }

  return (
    <>
    <Section
      titre={`Paiements (${liste.length})`}
      description={`${formaterMontant(somme(encaisses))} encaissés · ${formaterMontant(somme(aVenir))} prévus`}
      action={bouton}
    >
      <>
        {aVenir.length > 0 && (
          <>
            <div className="bg-amber-50 px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-amber-800">
              À venir — {aVenir.length} échéance(s), {formaterMontant(somme(aVenir))}
            </div>
            <ul className="divide-y divide-encre-100">{aVenir.map(ligne)}</ul>
          </>
        )}
        {/* Un bandeau pour les encaisses aussi (2026-10-07) : sans lui, le
              dernier prevu et le premier encaisse se suivaient sans rien
              pour marquer la frontiere — la pastille de chaque ligne ne
              suffisait pas a la lecture (ALLOUCH Rafael). Vert, comme le
              statut ; ambre pour les echeances a venir. */}
        {encaisses.length > 0 && (
          <>
            <div className="bg-emerald-50 px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-emerald-800">
              Encaissés — {encaisses.length} règlement(s), {formaterMontant(somme(encaisses))}
            </div>
            <ul className="divide-y divide-encre-100">{encaisses.map(ligne)}</ul>
          </>
        )}
      </>
    </Section>
    {formulaire}
    </>
  );
}

/**
 * Inscriptions de l'adhérent sur l'année active (M3). La création se fait
 * ici, la personne étant déjà connue : c'est le parcours du CDC —
 * personne → activité → créneau → montant.
 */
function InscriptionsDeLAdherent({
  personne,
  onChange,
  version,
  nonAffecte,
  onEssai,
}: {
  personne: PersonneDetail;
  onChange: () => void;
  /** Incremente par la page a chaque reglement enregistre. */
  version: number;
  /** Recu pour cet adherent, pas encore ventile. Voir `onNonAffecte`. */
  nonAffecte: number;
  /** Publie le montant du sur l'annee (cotisation comprise), `null` tant qu'il n'est pas charge. Voir `du` du bloc Paiements. */
  /** Publie a la page si un forfait d'essai est en cours ou echu sans confirmation (2026-11-04). */
  onEssai: (essaiEnCours: boolean) => void;
}) {
  const { peut, entiteActive } = useAuth();
  const [modaleOuverte, setModaleOuverte] = useState(false);
  // Le clic sur une ligne ouvre le MEME formulaire, avec l'inscription.
  const [edition, setEdition] = useState<Inscription | null>(null);
  const [erreurSuppression, setErreurSuppression] = useState<string | null>(null);
  // Les deux actes de gestion, ouverts depuis le menu de la modale
  // d'edition. La fiche ne les proposait pas : il fallait retourner sur
  // la liste Inscriptions pour transferer ou arreter un cours.
  const [transfert, setTransfert] = useState<Inscription | null>(null);
  const [finAnticipee, setFinAnticipee] = useState<Inscription | null>(null);
  // CONFIRMER L'ESSAI (2026-10-08) : l'eleve poursuit, le plein tarif
  // s'applique — le reste a payer de la fiche change avec.
  const confirmerEssai = async (i: Inscription) => {
    if (!window.confirm(`Confirmer que l’élève poursuit « ${i.activite_libelle} » au plein tarif ?`)) return;
    await api.post(`/api/inscriptions/${i.id}/confirmer-essai`, {});
    setEdition(null);
    inscriptions.recharger();
    onChange();
  };

  const cle = entiteActive?.id ?? '';
  // Idem pour les inscriptions : elles suivent l'annee du bandeau.
  const annee = useAnnee();
  const activites = useRessource(
    () =>
      peut('activite.lire')
        ? api.get<Activite[]>('/api/activites', { anneeId: annee.anneeId })
        : Promise.resolve([]),
    [cle, annee.anneeId],
  );

  // La grille complete de l'annee, pour le choix d'un creneau de
  // destination : chargee SEULEMENT quand le transfert est demande.
  const creneauxTransfert = useRessource(
    () =>
      transfert && annee.anneeId
        ? api.get<Creneau[]>('/api/creneaux', { anneeId: annee.anneeId })
        : Promise.resolve([]),
    [cle, transfert?.id, annee.anneeId],
  );

  // `version` change quand un reglement est enregistre : il force la
  // relecture des montants payes, que rien d'autre ne signalerait —
  // `personne.id` et l'annee, eux, n'ont pas bouge.
  const inscriptions = useRessource(
    () =>
      peut('inscription.lire') && annee.annee
        ? api.get<Inscription[]>('/api/inscriptions', {
            anneeId: annee.anneeId,
            personneId: personne.id,
          })
        : Promise.resolve([]),
    [cle, annee.anneeId, personne.id, version],
  );

  /**
   * Ce que l'adherent doit sur l'annee, COTISATION COMPRISE (decision du
   * 2026-08-29). Le tableau de bord compte deja l'adhesion dans la dette
   * totale : l'en omettre ici faisait dire deux montants differents pour
   * la meme annee.
   *
   * On lit `adhesion_montant` de la fiche plutot que de refaire la
   * requete : il est derive sur l'annee affichee, comme la pastille.
   */
  const cotisation = personne.membre_actif ? (personne.adhesion_montant ?? 0) : 0;
  // Publie a la page par un EFFET, avant le retour anticipe (un hook ne se
  // declare pas apres), comme `nonAffecte` dans l'autre sens : un forfait
  // d'essai en cours ou echu sans confirmation, sur une inscription non
  // inactive — la regle du PDF (2026-11-04).
  const essaiEnCours = (inscriptions.donnees ?? []).some(
    (i) => i.statut !== 'inactif' && (i.essai_statut === 'en_cours' || i.essai_statut === 'echu'),
  );
  const publierEssai = useRef(onEssai);
  publierEssai.current = onEssai;
  useEffect(() => {
    publierEssai.current(essaiEnCours);
  }, [essaiEnCours]);

  if (!peut('inscription.lire')) return null;

  const liste = inscriptions.donnees ?? [];
  // Sur le montant EXIGIBLE (2026-10-08) : pendant un forfait d'essai non
  // confirme, l'eleve ne doit que le forfait ; confirme, le du entier.
  const total = liste.reduce((somme, i) => somme + i.montant_exigible, 0) + cotisation;

  /**
   * CE QUI EST COUVERT, cotisation comprise. `montant_regle` porte la
   * somme des lignes de `reglement_inscription` : elle compte les
   * reglements PREVUS autant que les encaisses — c'est une allocation,
   * pas un encaissement. Un cheque remis en septembre pour toute l'annee
   * couvre des le premier jour ce qu'il paiera en juin.
   *
   * La cotisation entre dans les deux membres ou dans aucun : elle est
   * dans `total`, sa part reglee doit donc l'etre dans `paye`, faute de
   * quoi tout membre actif afficherait 20 EUR de reste a payer.
   */
  const cotisationReglee = personne.membre_actif ? (personne.adhesion_reglee ?? 0) : 0;

  /**
   * « PAYE » EST CE QUI EST ARRIVE POUR LUI, pas seulement ce qui est
   * ventile (definition du 2026-09-02). Les allocations, plus la part
   * recue mais pas encore affectee — sans quoi un virement de 280 EUR
   * sur un reste de 269 laissait 11 EUR invisibles : la fiche annoncait
   * « Payé 2 270 € » ici et « 2 281 € encaissés » au bloc Paiements,
   * sans rien pour expliquer l'ecart.
   *
   * Le reste et le trop-percu sont les DEUX SIGNES du meme calcul,
   * `total - paye`, affiches en valeur absolue : rouge s'il reste a
   * payer, vert si l'on a trop recu. Jamais les deux, jamais a zero.
   */
  const paye =
    liste.reduce((somme, i) => somme + i.montant_regle, 0) + cotisationReglee + nonAffecte;
  const ecart = total - paye;
  const reste = Math.max(0, ecart);
  const tropPercu = Math.max(0, -ecart);

  /**
   * Une inscription ne se supprime que TANT QU'AUCUN REGLEMENT ne la
   * couvre : au-dela, la supprimer laisserait un encaissement sans
   * contrepartie, et la base le refuse de toute facon (ON DELETE RESTRICT
   * sur reglement_inscription). Passe ce cap, l'arret d'un eleve se dit
   * par une date de fin anticipee (RG-03), pas par un effacement.
   *
   * On lit `reglements` et non `montant_regle` : ce dernier ecarte les
   * reglements annules, dont la ligne de rattachement subsiste et bloque.
   *
   * L'annee figee (RG-27) retire aussi le bouton — le serveur y refuse
   * toute ecriture.
   */
  const supprimable = (inscription: Inscription) =>
    peut('inscription.supprimer') && annee.modifiable && inscription.reglements === 0;

  const supprimer = async (inscription: Inscription) => {
    if (
      !window.confirm(
        `Supprimer l’inscription de ${personne.prenom} ${personne.nom} en ` +
          `« ${inscription.activite_libelle} » ? Cette action est definitive.`,
      )
    ) {
      return;
    }

    setErreurSuppression(null);
    try {
      await api.delete(`/api/inscriptions/${inscription.id}`);
      inscriptions.recharger();
      onChange();
    } catch (cause) {
      setErreurSuppression(
        cause instanceof ErreurApi ? cause.message : 'Suppression impossible.',
      );
    }
  };

  return (
    <>
      {liste.length === 0 ? (
        <SectionVide
          message="Aucune inscription sur l’année active."
          action={
            peut('inscription.creer') && annee.annee ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModaleOuverte(true)}
              >
                Inscrire
              </button>
            ) : null
          }
        />
      ) : (
        <Section
          titre={`Inscriptions (${liste.length})`}
          description={
            <>
              Total dû {formaterMontant(total)}
              {cotisation > 0 && ` (dont ${formaterMontant(cotisation)} de cotisation)`} · Payé{' '}
              {formaterMontant(paye)}
              {/* A zero, ces deux mentions ne disent rien que les deux
                  montants ci-dessus ne disent deja — et « Reste a payer
                  0,00 EUR » en rouge se lirait comme une alerte. */}
              {reste > 0 && (
                <>
                  {' · '}
                  <span className="font-medium text-red-800">
                    Reste à payer {formaterMontant(reste)}
                  </span>
                </>
              )}
              {tropPercu > 0 && (
                <>
                  {' · '}
                  <span className="font-medium text-emerald-700">
                    Trop perçu {formaterMontant(tropPercu)}
                  </span>
                </>
              )}
            </>
          }
          action={
            peut('inscription.creer') && annee.annee ? (
              <button
                type="button"
                className="bouton-secondaire py-1.5"
                onClick={() => setModaleOuverte(true)}
              >
                Inscrire
              </button>
            ) : null
          }
        >
          <ul className="divide-y divide-encre-100">
            {liste.map((inscription) => (
              <li
                key={inscription.id}
                className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                  peut('inscription.modifier') ? 'cursor-pointer hover:bg-encre-50' : ''
                }`}
                onClick={peut('inscription.modifier') ? () => setEdition(inscription) : undefined}
              >
                {/* Une seule ligne, meme echelle que le reste de la fiche :
                      l'atelier a 14 px, le creneau a 12 px et en gris. Et
                      comme dans la liste des creneaux, l'heure de DEBUT
                      suivie de la duree — la fin en decoule. */}
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{inscription.activite_libelle}</span>
                  {inscription.pupitre && (
                    <span className="text-xs text-encre-500">pupitre {inscription.pupitre}</span>
                  )}
                  {/* Une inscription est active par defaut : le dire a
                        chaque ligne noie le seul cas qui merite l'oeil. */}
                  {inscription.statut !== 'actif' && (
                    <Badge ton="danger">{libelleStatut(inscription.statut)}</Badge>
                  )}
                  {/* Le forfait d'essai se lit ICI aussi (2026-10-02) : la
                      liste des adherents le marquait d'un tag que rien, sur
                      la fiche, ne venait expliquer. Meme dessin que le tag
                      de la liste et du planning, avec la fin de l'essai. */}
                  {/* L'ETAT de l'essai (2026-10-08) : bleu en cours, rouge
                      echu — la fin est passee sans confirmation —, et plus
                      de tag une fois confirme. */}
                  {inscription.essai_statut === 'en_cours' && (
                    <span className="shrink-0 rounded border border-blue-400 bg-white px-1 text-[10px] font-medium leading-tight text-blue-700">
                      forfait d’essai
                      {inscription.date_fin_essai
                        ? ` → ${formaterDate(inscription.date_fin_essai)}`
                        : ''}
                    </span>
                  )}
                  {inscription.essai_statut === 'echu' && (
                    <span className="shrink-0 rounded border border-red-400 bg-white px-1 text-[10px] font-medium leading-tight text-red-700">
                      forfait d’essai terminé
                      {inscription.date_fin_essai
                        ? ` le ${formaterDate(inscription.date_fin_essai)}`
                        : ''}
                    </span>
                  )}
                  {peut('inscription.modifier') &&
                    (inscription.essai_statut === 'en_cours' || inscription.essai_statut === 'echu') && (
                      <Infobulle texte="L’élève poursuit : le plein tarif s’applique" className="inline-flex shrink-0">
                        <button
                          type="button"
                          className={`rounded border px-1.5 text-[11px] font-medium leading-snug ${
                            inscription.essai_statut === 'echu'
                              ? 'border-red-300 text-red-700 hover:bg-red-50'
                              : 'border-encre-300 text-encre-600 hover:bg-encre-100'
                          }`}
                          onClick={(evenement) => {
                            evenement.stopPropagation();
                            void confirmerEssai(inscription);
                          }}
                        >
                          Confirmer
                        </button>
                      </Infobulle>
                    )}
                  {inscription.creneau_id ? (
                    <span className="truncate text-xs text-encre-500">
                      {libelleJour(inscription.jour)}{' '}
                      <span className="tabulaire">{formaterHeure(inscription.heure_debut)}</span> (
                      {formaterDuree(inscription.duree_minutes)})
                      {inscription.professeur_nom && ` · ${inscription.professeur_nom}`}
                      {inscription.lieu_code && ` · ${inscription.lieu_code}`}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-700">Sans créneau identifié</span>
                  )}
                </div>

                {/* Le du, puis ce qui le couvre. ALLOUCH 2025-26 est le
                    cas revelateur : deux inscriptions, cinq reglements,
                    des repartitions differentes — 1 860 EUR couverts sur
                    le piano, 141 sur 410 pour l'atelier. Sans cette
                    mention, la ligne ne dit pas ou va l'argent. */}
                <span className="flex shrink-0 items-baseline gap-1.5 text-sm">
                  {/* Ce qui couvre A GAUCHE, le du a DROITE : les montants
                      dus restent ainsi alignes d'une ligne a l'autre, ce
                      qu'une mention de longueur variable placee apres eux
                      empechait. */}
                  {inscription.montant_regle > 0 && (
                    <span className="tabulaire text-xs text-encre-400">
                      (payé {formaterMontant(inscription.montant_regle)})
                    </span>
                  )}
                  <span className="tabulaire font-medium">
                    {formaterMontant(inscription.montant_exigible)}
                  </span>
                  {/* La reduction, par type, en infobulle (2026-10-12) ;
                      ambre quand une part n'est detaillee par aucun type. */}
                  {inscription.reduction > 0 && (
                    <Infobulle texte={decrireReductions(inscription)}>
                      <Badge ton={inscription.reduction_non_detaillee > 0 ? 'alerte' : 'neutre'}>
                        réduit
                      </Badge>
                    </Infobulle>
                  )}
                  {/* Pendant l'essai, le forfait est ce qui est du ; le
                      tarif de l'annee reste dit, en petit. */}
                  {inscription.montant_exigible !== inscription.montant_du && (
                    <span className="tabulaire text-xs text-encre-400">
                      (forfait · {formaterMontant(inscription.montant_du)} à l’année)
                    </span>
                  )}
                </span>

                {/* La ligne entiere ouvre la modification : le clic sur la
                    croix ne doit pas la declencher au passage. */}
                {supprimable(inscription) && (
                  <Infobulle texte="Supprimer cette inscription" className="inline-flex shrink-0">
                    <button
                      type="button"
                      className="rounded px-1.5 text-encre-300 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Supprimer l’inscription en ${inscription.activite_libelle}`}
                      onClick={(evenement) => {
                        evenement.stopPropagation();
                        void supprimer(inscription);
                      }}
                    >
                      ✕
                    </button>
                  </Infobulle>
                )}
              </li>
            ))}
          </ul>

          {erreurSuppression && (
            <p
              className="border-t border-encre-100 px-4 py-3 text-sm text-red-800"
              role="alert"
            >
              {erreurSuppression}
            </p>
          )}
        </Section>
      )}

      {annee.annee && (
        <FormulaireInscription
          ouverte={modaleOuverte || edition !== null}
          anneeId={annee.anneeId}
          activites={activites.donnees ?? []}
          personneImposee={personne}
          inscription={edition}
          onFermer={() => {
            setModaleOuverte(false);
            setEdition(null);
          }}
          onEnregistre={() => {
            setModaleOuverte(false);
            setEdition(null);
            inscriptions.recharger();
            onChange();
          }}
          onTransferer={setTransfert}
          onFinAnticipee={setFinAnticipee}
          onConfirmerEssai={(i) => void confirmerEssai(i)}
          onRouvrir={async (i) => {
            await api.post(`/api/inscriptions/${i.id}/reouverture`, {});
            setModaleOuverte(false);
            setEdition(null);
            inscriptions.recharger();
            onChange();
          }}
        />
      )}

      {/* Les deux actes de gestion. Le menu de la modale d'edition ferme
          celle-ci avant d'ouvrir l'une d'elles : jamais deux modales a la
          fois. Elles rechargent la fiche entiere — un transfert change
          les montants, et la cotisation avec eux. */}
      <FormulaireTransfert
        inscription={transfert}
        creneaux={creneauxTransfert.donnees ?? []}
        activites={activites.donnees ?? []}
        onFermer={() => setTransfert(null)}
        onEnregistre={() => {
          setTransfert(null);
          inscriptions.recharger();
          onChange();
        }}
      />

      <FormulaireFinAnticipee
        inscription={finAnticipee}
        onFermer={() => setFinAnticipee(null)}
        onEnregistre={() => {
          setFinAnticipee(null);
          inscriptions.recharger();
          onChange();
        }}
      />
    </>
  );
}

// --- Fiche de l'adhérent ----------------------------------------------

/**
 * Coordonnées de l'adhérent lui-même, à distinguer de ses contacts.
 *
 * Un adulte inscrit seul se joint directement : lui créer un « contact »
 * qui est lui-même est un artefact du classeur, pas une réalité.
 */
function FormulaireFiche({
  ouverte,
  personne,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  personne: PersonneDetail;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  // Le statut associatif se saisit sur l'annee AFFICHEE, pas sur l'annee
  // active : la pastille de la fiche suit le bandeau, la case doit la
  // suivre aussi. Les faire diverger montrait « membre actif » en 2026-27
  // tout en modifiant l'adhesion de 2025-26.
  const annee = useAnnee();
  const [valeurs, setValeurs] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    email: '',
    telephone: '',
    telephonePays: 'FR',
    ecoleFrequentee: '',
    civilite: '',
  });
  const [membre, setMembre] = useState<SaisieMembre>({ cotise: false });
  const [droitImage, setDroitImage] = useState<SaisieDroitImage>(saisieDepuisDroitImage(null));
  const [handicap, setHandicap] = useState<SaisieHandicap>({ handicap: null, precision: '' });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  // La fiche transporte `adhesion_id` (derive, meme source que la
  // pastille) : il est deja la quand la modale s'ouvre. C'est ce qui
  // referme le defaut du 2026-08-29 — voir `enregistrerMembre`.
  const adhesionId = personne.adhesion_id ?? null;

  const cle = ouverte ? personne.id : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      nom: personne.nom,
      prenom: personne.prenom,
      dateNaissance: personne.date_naissance ?? '',
      email: personne.email ?? '',
      telephone: telephonePourSaisie(personne.telephone, personne.telephone_pays),
      telephonePays: personne.telephone_pays ?? 'FR',
      ecoleFrequentee: personne.ecole_frequentee ?? '',
      civilite: personne.civilite ?? '',
    });
    setMembre({ cotise: adhesionId !== null });
    // Meme source que la pastille : la fiche porte le droit a l'image de
    // l'annee affichee (2026-10-13).
    setDroitImage(saisieDepuisDroitImage(personne.droit_image));
    setHandicap({
      handicap: personne.handicap,
      precision: personne.handicap_precision ?? '',
    });
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.patch(`/api/personnes/${personne.id}`, {
        nom: valeurs.nom,
        prenom: valeurs.prenom,
        dateNaissance: valeurs.dateNaissance || null,
        email: valeurs.email || null,
        telephone: valeurs.telephone || null,
        telephonePays: valeurs.telephonePays,
        ecoleFrequentee: valeurs.ecoleFrequentee || null,
        // Toujours envoyee, meme a null : « non renseignee » dans la
        // liste doit pouvoir EFFACER une deduction erronee.
        civilite: valeurs.civilite || null,
        // Toujours envoyes, meme a null : c'est ce qui permet de RETIRER
        // une situation declaree par erreur. Une cle absente laisserait
        // la colonne en place.
        handicap: handicap.handicap,
        handicapPrecision: handicap.handicap ? handicap.precision || null : null,
      });
      // La cotisation vit dans `adhesion`, la fiche dans `personne` :
      // deux tables, donc deux appels. L'ordre importe peu, aucun ne
      // depend de l'autre.
      await enregistrerMembre(
        personne.id,
        membre,
        { cotise: adhesionId !== null, adhesionId },
        annee.annee
          ? { id: annee.annee.id, montantAdhesion: annee.annee.montant_adhesion }
          : null,
      );
      // Le droit a l'image vit dans sa table, par annee : troisieme
      // appel, qui ne part que si la saisie a change.
      await enregistrerDroitImage(
        personne.id,
        droitImage,
        saisieDepuisDroitImage(personne.droit_image),
        annee.annee?.id ?? null,
      );
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Modifier la fiche" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Nom">
            <input
              className="champ uppercase"
              value={valeurs.nom}
              onChange={(e) => setValeurs({ ...valeurs, nom: e.target.value })}
              required
              autoFocus
            />
          </Champ>
          <Champ libelle="Prénom">
            <input
              className="champ"
              value={valeurs.prenom}
              onChange={(e) => setValeurs({ ...valeurs, prenom: e.target.value })}
              required
            />
          </Champ>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Date de naissance">
            <input
              className="champ"
              type="date"
              value={valeurs.dateNaissance}
              onChange={(e) => setValeurs({ ...valeurs, dateNaissance: e.target.value })}
            />
          </Champ>
          <Champ libelle="Civilité">
            <select
              className="champ"
              value={valeurs.civilite}
              onChange={(e) => setValeurs({ ...valeurs, civilite: e.target.value })}
            >
              <option value="">Non renseignée</option>
              <option value="M">M.</option>
              <option value="Mme">Mme</option>
            </select>
          </Champ>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Email">
            <input
              className="champ"
              type="email"
              value={valeurs.email}
              onChange={(e) => setValeurs({ ...valeurs, email: e.target.value })}
            />
          </Champ>
          <ChampTelephone
            numero={valeurs.telephone}
            pays={valeurs.telephonePays}
            onChange={(telephone, telephonePays) =>
              setValeurs({ ...valeurs, telephone, telephonePays })
            }
          />
        </div>

        {/* Sous les coordonnees, et MASQUEE POUR LES ADULTES (2026-10-13) :
            l'ecole frequentee est une donnee d'enfant. Sans date de
            naissance, on ne conclut pas et le champ reste visible. */}
        {estMineur(valeurs.dateNaissance) !== false && (
          <ChampEtablissement
            valeur={valeurs.ecoleFrequentee}
            onChanger={(ecoleFrequentee) => setValeurs({ ...valeurs, ecoleFrequentee })}
            valide={personne.etablissement_valide}
          />
        )}

        <ChampMembre
          valeur={membre}
          onChanger={setMembre}
          anneeLibelle={annee.annee?.libelle ?? null}
          montantAdhesion={annee.annee?.montant_adhesion ?? null}
          inscrit={personne.adherent_entite}
          verrouille={!annee.modifiable}
        />

        <ChampDroitImage
          valeur={droitImage}
          onChanger={setDroitImage}
          anneeLibelle={annee.annee?.libelle ?? null}
          verrouille={!annee.modifiable}
        />

        <ChampHandicap valeur={handicap} onChanger={setHandicap} />

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}

// --- Rattachement d'un contact ----------------------------------------

function FormulaireContact({
  ouverte,
  personneId,
  contact,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  personneId: string;
  /** `null` pour un rattachement ; le contact rattache pour une modification. */
  contact?: ContactRattache | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    telephonePays: 'FR',
    qualite: 'mere' as QualiteContact,
    estReferent: false,
  });
  const [recherche, setRecherche] = useState('');
  const [existants, setExistants] = useState<Contact[]>([]);
  const [choisi, setChoisi] = useState<Contact | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = ouverte ? (contact?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      nom: contact?.nom ?? '',
      prenom: contact?.prenom ?? '',
      email: contact?.email ?? '',
      telephone: telephonePourSaisie(contact?.telephone, contact?.telephone_pays),
      telephonePays: contact?.telephone_pays ?? 'FR',
      qualite: contact?.qualite ?? 'mere',
      estReferent: contact?.est_referent ?? false,
    });
    setRecherche('');
    setExistants([]);
    setChoisi(null);
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const chercher = async (terme: string) => {
    setRecherche(terme);
    if (terme.length < 3) {
      setExistants([]);
      return;
    }
    setExistants(await api.get<Contact[]>('/api/contacts', { recherche: terme }));
  };

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      if (contact) {
        // Deux objets : le CONTACT est partage et se corrige pour tous ;
        // la qualite et le caractere referent qualifient le seul lien a
        // cet adherent — une meme personne peut etre mere de l'une et
        // tutrice de l'autre.
        await api.patch(`/api/contacts/${contact.id}`, {
          nom: valeurs.nom || null,
          prenom: valeurs.prenom || null,
          email: valeurs.email || null,
          telephone: valeurs.telephone || null,
          telephonePays: valeurs.telephonePays,
        });
        await api.patch(`/api/personnes/${personneId}/contacts/${contact.id}`, {
          qualite: valeurs.qualite,
          estReferent: valeurs.estReferent,
        });
      } else {
        await api.post(`/api/personnes/${personneId}/contacts`, {
          ...(choisi
            ? { contactId: choisi.id }
            : {
                nom: valeurs.nom || null,
                prenom: valeurs.prenom || null,
                email: valeurs.email || null,
                telephone: valeurs.telephone || null,
                telephonePays: valeurs.telephonePays,
              }),
          qualite: valeurs.qualite,
          estReferent: valeurs.estReferent,
        });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Rattachement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={contact ? 'Modifier le contact' : 'Rattacher un contact'}
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        {/* La recherche n'a de sens qu'au rattachement : en modification,
            le contact est deja designe. */}
        {!contact && (
          <Champ
            libelle="Chercher un contact existant"
            aide="Une mère déjà saisie pour un autre enfant se réutilise : ne la ressaisissez pas."
          >
            <input
              className="champ"
              value={recherche}
              onChange={(e) => void chercher(e.target.value)}
              placeholder="Nom, email ou téléphone…"
            />
          </Champ>
        )}

        {contact && (contact.nb_rattachements ?? 1) > 1 && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Ce contact est partagé avec {(contact.nb_rattachements ?? 1) - 1} autre
            {(contact.nb_rattachements ?? 1) > 2 ? 's' : ''} adhérent
            {(contact.nb_rattachements ?? 1) > 2 ? 's' : ''} : le corriger ici le corrige partout.
            La qualité et le caractère référent, eux, ne valent que pour cette fiche.
          </p>
        )}

        {existants.length > 0 && (
          <ul className="max-h-40 divide-y divide-encre-100 overflow-y-auto rounded-md border border-encre-200">
            {existants.map((contact) => (
              <OptionSelectionnable
                key={contact.id}
                choisi={choisi?.id === contact.id}
                onBasculer={() => setChoisi(choisi?.id === contact.id ? null : contact)}
              >
                {[contact.prenom, contact.nom].filter(Boolean).join(' ') || '(sans nom)'}
                <span className="ml-2 text-xs text-encre-500">
                  {contact.email ?? formaterTelephone(contact.telephone, contact.telephone_pays)}
                </span>
                {(contact.nb_rattachements ?? 0) > 0 && (
                  <span className="ml-2 text-xs text-encre-400">
                    déjà rattaché à {contact.nb_rattachements}
                  </span>
                )}
              </OptionSelectionnable>
            ))}
          </ul>
        )}

        {!choisi && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Champ libelle="Nom">
                <input
                  className="champ"
                  value={valeurs.nom}
                  onChange={(e) => setValeurs({ ...valeurs, nom: e.target.value })}
                />
              </Champ>
              <Champ libelle="Prénom">
                <input
                  className="champ"
                  value={valeurs.prenom}
                  onChange={(e) => setValeurs({ ...valeurs, prenom: e.target.value })}
                />
              </Champ>
            </div>

            <Champ libelle="Email" aide="Email ou téléphone : au moins l’un des deux.">
              <input
                className="champ"
                type="email"
                value={valeurs.email}
                onChange={(e) => setValeurs({ ...valeurs, email: e.target.value })}
              />
            </Champ>

            <ChampTelephone
              numero={valeurs.telephone}
              pays={valeurs.telephonePays}
              onChange={(telephone, telephonePays) =>
                setValeurs({ ...valeurs, telephone, telephonePays })
              }
            />
          </>
        )}

        <Champ
          libelle="Qualité"
          aide="Elle qualifie ce lien-ci : la même personne peut être mère de l’un et tutrice de l’autre."
        >
          <select
            className="champ"
            value={valeurs.qualite}
            onChange={(e) =>
              setValeurs({
                ...valeurs,
                qualite: e.target.value as QualiteContact,
              })
            }
          >
            {QUALITES.map((qualite) => (
              <option key={qualite} value={qualite}>
                {qualite}
              </option>
            ))}
          </select>
        </Champ>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={valeurs.estReferent}
            onChange={(e) => setValeurs({ ...valeurs, estReferent: e.target.checked })}
          />
          Contact référent de cet adhérent
        </label>

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} libelle="Rattacher" />
      </form>
    </Modale>
  );
}

// --- Rattachement d'une adresse ---------------------------------------

function FormulaireAdresse({
  ouverte,
  personneId,
  adresse,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  personneId: string;
  /** `null` pour un ajout ; l'adresse rattachee pour une modification. */
  adresse?: AdresseRattachee | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    ligne1: '',
    ligne2: '',
    codePostal: '',
    ville: '',
    rang: 'primaire' as 'primaire' | 'secondaire',
    commentaire: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);
  /**
   * Le serveur refuse une saisie qui rejoint une adresse existante, et
   * demande confirmation. Cochee, elle autorise la FUSION des deux fiches
   * — meme procede que le doublon de cheque (RG-12).
   */
  const [fusionner, setFusionner] = useState(false);

  // Reinitialisation au rendu, sans useEffect : la cle suit l'objet edite.
  const cle = ouverte ? (adresse?.id ?? 'nouvelle') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setFusionner(false);
    setValeurs({
      ligne1: adresse?.ligne1 ?? '',
      ligne2: adresse?.ligne2 ?? '',
      codePostal: adresse?.code_postal ?? '',
      ville: adresse?.ville ?? '',
      rang: adresse?.rang ?? 'primaire',
      commentaire: adresse?.commentaire ?? '',
    });
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      if (adresse) {
        // Deux objets distincts, donc deux appels : l'ADRESSE est partagee
        // et se corrige pour tous ; le RANG et le COMMENTAIRE qualifient le
        // seul rattachement de cet adherent.
        // La fusion fait DISPARAITRE la ligne modifiee au profit de sa
        // jumelle : le rattachement se pose donc sur l'identifiant rendu
        // par le serveur, jamais sur celui qu'on avait en main.
        const survivante = await api.patch<Adresse>(`/api/adresses/${adresse.id}`, {
          ligne1: valeurs.ligne1,
          ligne2: valeurs.ligne2 || null,
          codePostal: valeurs.codePostal || null,
          ville: valeurs.ville || null,
          fusionner,
        });
        await api.patch(`/api/personnes/${personneId}/adresses/${survivante.id}`, {
          rang: valeurs.rang,
          commentaire: valeurs.commentaire || null,
        });
      } else {
        await api.post(`/api/personnes/${personneId}/adresses`, {
          ligne1: valeurs.ligne1,
          ligne2: valeurs.ligne2 || null,
          codePostal: valeurs.codePostal || null,
          ville: valeurs.ville || null,
          rang: valeurs.rang,
          commentaire: valeurs.commentaire || null,
        });
      }
      onEnregistre();
    } catch (cause) {
      const message = cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.';
      setErreur(message);
      if (message.includes('existe deja')) setFusionner(false);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={adresse ? 'Modifier l’adresse' : 'Ajouter une adresse'}
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        <Champ
          libelle="Adresse"
          aide={
            adresse
              ? 'L’adresse est partagée : la corriger ici la corrige pour tous les adhérents qui s’y rattachent.'
              : 'Une adresse identique à une existante sera réutilisée plutôt que dupliquée.'
          }
        >
          <input
            className="champ"
            value={valeurs.ligne1}
            onChange={(e) => setValeurs({ ...valeurs, ligne1: e.target.value })}
            required
            autoFocus
          />
        </Champ>

        <Champ libelle="Complément">
          <input
            className="champ"
            value={valeurs.ligne2}
            onChange={(e) => setValeurs({ ...valeurs, ligne2: e.target.value })}
          />
        </Champ>

        <div className="grid grid-cols-3 gap-3">
          <Champ libelle="Code postal">
            <input
              className="champ tabulaire"
              value={valeurs.codePostal}
              onChange={(e) => setValeurs({ ...valeurs, codePostal: e.target.value })}
              inputMode="numeric"
              maxLength={5}
            />
          </Champ>
          <div className="col-span-2">
            <Champ libelle="Ville">
              <input
                className="champ"
                value={valeurs.ville}
                onChange={(e) => setValeurs({ ...valeurs, ville: e.target.value })}
              />
            </Champ>
          </div>
        </div>

        <Champ libelle="Rang">
          <select
            className="champ"
            value={valeurs.rang}
            onChange={(e) =>
              setValeurs({
                ...valeurs,
                rang: e.target.value as 'primaire' | 'secondaire',
              })
            }
          >
            <option value="primaire">Primaire — adresse de référence</option>
            <option value="secondaire">Secondaire</option>
          </select>
        </Champ>

        <Champ libelle="Commentaire" aide="Par exemple « chez la mère », « garde alternée ».">
          <input
            className="champ"
            value={valeurs.commentaire}
            onChange={(e) => setValeurs({ ...valeurs, commentaire: e.target.value })}
          />
        </Champ>

        <PiedFormulaire
          erreur={erreur}
          enCours={enCours}
          onFermer={onFermer}
          enfants={
            /* Proposee seulement apres le refus : une case offerte d'emblee
               inviterait a fusionner deux adresses qui n'en sont qu'une par
               coincidence de libelle. */
            erreur?.includes('existe deja') ? (
              <label className="flex items-start gap-2 text-sm text-encre-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={fusionner}
                  onChange={(e) => setFusionner(e.target.checked)}
                />
                <span>
                  Fusionner avec l’adresse existante — les deux fiches n’en feront plus qu’une, et
                  tous ceux qui y sont rattachés suivront le libellé saisi ici.
                </span>
              </label>
            ) : null
          }
        />
      </form>
    </Modale>
  );
}

// --- Lien de parenté ---------------------------------------------------

/**
 * Creation ET correction d'un lien de parente.
 *
 * La reprise a deduit 26 fratries du seul nom de famille : deux homonymes
 * sans parente s'y retrouvent relies a tort, et un pere y passe pour un
 * frere. Le corriger passe par le MEME formulaire que la creation, comme
 * pour les adresses, les contacts et les inscriptions.
 *
 * En correction, l'autre bout n'est PAS modifiable : changer la personne
 * reliee, c'est un autre lien. On le retire, on en ajoute un.
 */
/**
 * Ligne selectionnable d'un selecteur (contact a rattacher, personne a
 * relier).
 *
 * POURQUOI CE COMPOSANT EXISTE. Les deux selecteurs marquaient le choix par
 * `bg-encre-100` tout en gardant `hover:bg-encre-50`. Or Tailwind emet la
 * variante `hover:` APRES l'utilitaire de base, et `.classe:hover` est plus
 * specifique : au survol — c'est-a-dire juste apres le clic, la souris
 * n'ayant pas bouge — le fond du choix redevenait celui de n'importe quelle
 * ligne survolee. Restait `font-medium` pour toute difference, invisible a
 * l'oeil. Le clic marchait, rien ne le montrait, et on croyait la liste
 * inerte.
 *
 * Le survol ne s'applique donc plus a la ligne choisie, et une coche dit
 * explicitement laquelle l'est.
 */
function OptionSelectionnable({
  choisi,
  survole = false,
  rang,
  onSurvoler,
  onBasculer,
  children,
}: {
  choisi: boolean;
  /** Element vise par le clavier — distinct de `choisi`, qui est valide. */
  survole?: boolean;
  rang?: number;
  onSurvoler?: () => void;
  onBasculer: () => void;
  children: ReactNode;
}) {
  return (
    <li data-index={rang}>
      <button
        type="button"
        aria-pressed={choisi}
        className={`flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm ${
          choisi
            ? 'bg-emerald-50 font-medium text-emerald-900'
            : survole
              ? 'bg-encre-100 text-encre-800'
              : 'text-encre-800 hover:bg-encre-50'
        }`}
        onMouseEnter={onSurvoler}
        onClick={onBasculer}
      >
        {/* Largeur reservee dans les deux cas : sans elle, le libelle se
            decale au clic et la liste sautille. */}
        <span aria-hidden className="w-3 shrink-0 text-emerald-600">
          {choisi ? '✓' : ''}
        </span>
        <span className="min-w-0 flex-1">{children}</span>
      </button>
    </li>
  );
}

function FormulaireLien({
  ouverte,
  personne,
  lien,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  personne: PersonneDetail;
  lien?: Lien | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [recherche, setRecherche] = useState('');
  const [candidats, setCandidats] = useState<Personne[]>([]);
  const [choisi, setChoisi] = useState<Personne | null>(null);
  const [relation, setRelation] = useState('fratrie');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  // Reinitialisation au rendu, sans useEffect : la cle suit l'objet edite.
  const cle = ouverte ? (lien?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setRelation(lien ? relationDe(lien) : 'fratrie');
    setRecherche('');
    setCandidats([]);
    setChoisi(null);
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const chercher = async (terme: string) => {
    setRecherche(terme);
    if (terme.length < 2) {
      setCandidats([]);
      return;
    }
    const resultats = await api.get<Personne[]>('/api/personnes', {
      recherche: terme,
      limite: 15,
    });
    setCandidats(resultats.filter((candidat) => candidat.id !== personne.id));
  };

  // La liste est a SELECTION : on bascule le choix plutot que de le poser.
  const basculer = (candidat: Personne) =>
    setChoisi((actuel) => (actuel?.id === candidat.id ? null : candidat));
  const clavier = useListeClavier(candidats, basculer);

  const choix = RELATIONS.find((candidat) => candidat.cle === relation)!;

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!lien && !choisi) {
      setErreur('Choisissez la personne à relier.');
      return;
    }
    setErreur(null);
    setEnCours(true);
    try {
      if (lien) {
        // `origineId` porte le sens : pour un type oriente, c'est la
        // personne qui est parent ou tuteur de l'autre.
        await api.patch(`/api/liens/${lien.id}`, {
          type: choix.type,
          origineId: choix.inverse ? lien.personne_id : personne.id,
        });
      } else if (choix.inverse) {
        // « Enfant de X » se stocke « X est le parent de… » : c'est le
        // meme lien, cree depuis l'autre bout.
        await api.post(`/api/personnes/${choisi!.id}/liens`, {
          personneCibleId: personne.id,
          type: choix.type,
        });
      } else {
        await api.post(`/api/personnes/${personne.id}/liens`, {
          personneCibleId: choisi!.id,
          type: choix.type,
        });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={lien ? 'Modifier le lien de parenté' : 'Ajouter un lien de parenté'}
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        {lien ? (
          <Champ
            libelle="Relié à"
            aide="Pour relier une autre personne, retirez ce lien et ajoutez-en un."
          >
            <p className="rounded-md border border-encre-200 bg-encre-50 px-3 py-2 text-sm">
              {lien.prenom} <span className="uppercase">{lien.nom}</span>
            </p>
          </Champ>
        ) : (
          <>
            <Champ libelle="Relier à">
              <input
                className="champ"
                value={recherche}
                onChange={(e) => void chercher(e.target.value)}
                onKeyDown={clavier.auClavier}
                placeholder="Nom ou prénom…"
                autoFocus
              />
            </Champ>

            {candidats.length > 0 && (
              <ul
                ref={clavier.liste}
                className="max-h-48 divide-y divide-encre-100 overflow-y-auto rounded-md border border-encre-200"
              >
                {candidats.map((candidat, rang) => (
                  <OptionSelectionnable
                    key={candidat.id}
                    choisi={choisi?.id === candidat.id}
                    survole={rang === clavier.index}
                    rang={rang}
                    onSurvoler={() => clavier.survoler(rang)}
                    onBasculer={() => basculer(candidat)}
                  >
                    {candidat.nom.toUpperCase()} {candidat.prenom}
                    <span className="ml-2 text-xs text-encre-500">
                      {formaterDate(candidat.date_naissance)}
                    </span>
                  </OptionSelectionnable>
                ))}
              </ul>
            )}
          </>
        )}

        <Champ libelle={`${personne.prenom} ${personne.nom.toUpperCase()} est…`} aide={choix.aide}>
          <select
            className="champ"
            value={relation}
            onChange={(e) => setRelation(e.target.value)}
            autoFocus={lien !== null && lien !== undefined}
          >
            {RELATIONS.map((candidat) => (
              <option key={candidat.cle} value={candidat.cle}>
                {candidat.libelle}
              </option>
            ))}
          </select>
        </Champ>

        <PiedFormulaire
          erreur={erreur}
          enCours={enCours}
          onFermer={onFermer}
          libelle={lien ? 'Enregistrer' : 'Relier'}
        />
      </form>
    </Modale>
  );
}

/**
 * Le commentaire de la fiche (2026-10-31) : une modale, comme le reste
 * de la fiche, pour profiter de la confirmation de sortie quand une
 * saisie est en cours. Le champ s'envoie TOUJOURS, vide compris :
 * c'est ainsi qu'un commentaire s'efface — le serveur rend `null`
 * quand il ne reste aucun texte.
 */
function FormulaireCommentaire({
  ouverte,
  personne,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  personne: PersonneDetail;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [html, setHtml] = useState('');
  const [initialisePour, setInitialisePour] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Meme garde que la fiche : le contenu se pose a l'ouverture, depuis
  // la donnee deja a l'ecran, et se repose a chaque reouverture.
  const cle = ouverte ? personne.id : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setHtml(personne.commentaire ?? '');
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.patch(`/api/personnes/${personne.id}`, { commentaire: html || null });
      onEnregistre();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Commentaires" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        {/* Pas de `Champ` ici : il enveloppe dans un <label>, et un clic
            dans la zone de texte activerait le premier bouton de la
            barre — Gras. Vecu le 2026-10-31. */}
        <div>
          <span className="etiquette">Commentaire</span>
          <EditeurTexteLeger valeur={html} onChange={setHtml} lignes={8} />
          <span className="mt-1 block text-xs text-encre-400">
            Gras, italique, souligné et alignement sont conservés ; le reste de la mise en forme est retiré à
            l’enregistrement.
          </span>
        </div>
        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}
